"use client"

import { useState, useEffect, useCallback } from "react"
import Image from "next/image"
import Link from "next/link"
import { ShoppingCart, ExternalLink, Loader2, Package, ArrowRight } from "lucide-react"
import { getDirectusImageUrl, type DirectusProduct } from "@/lib/directus"
import { contentAnalytics } from "@/lib/analytics"
import { useAffiliateImpression } from "@/hooks/use-affiliate-impression"

interface ComparisonTableProps {
  category?: string
  variant?: string // category from widget marker
  slug?: string
}

export function ComparisonTable({ category, variant, slug }: ComparisonTableProps) {
  const [products, setProducts] = useState<DirectusProduct[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const effectiveCategory = category || variant || ""

  const handleProductClick = useCallback(
    (product: DirectusProduct) => {
      contentAnalytics.affiliateLinkClicked(
        slug || "",
        product.name,
        product.affiliate_url || "",
        "comparison-table"
      )
      // Unified event, paired with the affiliate_product_shown impression below so
      // comparison-table CTR is computable in one query (same `surface`).
      contentAnalytics.affiliateProductClicked("comparison-table", {
        productName: product.name,
        affiliateLink: product.affiliate_url || "",
        placement: "comparison-table",
      })
    },
    [slug]
  )

  useEffect(() => {
    let cancelled = false

    async function fetchProducts() {
      if (!effectiveCategory) {
        setIsLoading(false)
        return
      }
      try {
        const params = new URLSearchParams({
          category: effectiveCategory,
          limit: "4",
        })
        if (slug) params.set("slug", slug)

        const response = await fetch(`/api/blog/product-recommendations?${params}`)
        if (!response.ok) throw new Error("Failed to fetch")

        const data = await response.json()
        if (!cancelled && data.products?.length > 0) {
          setProducts(data.products)
        }
      } catch {
        // Silent fail
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    const timer = setTimeout(fetchProducts, 150)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [effectiveCategory, slug])

  // One impression per table view (fires when the loaded table scrolls into view).
  // Same `surface` as the click event above so CTR joins cleanly. Hook runs
  // unconditionally (rules-of-hooks); ref only attaches to the real table below.
  const impressionRef = useAffiliateImpression("comparison-table", {
    slug,
    count: products.length,
    // Mirrors the value affiliateLinkClicked already sends. Without it the
    // impression lands under a null placement while the click lands under
    // "comparison-table", so placement-level CTR reads as 0 shown / n clicked.
    placement: "comparison-table",
    // One impression per TABLE, not per row — CTR math must not divide row-level
    // clicks by this count as if it were per-product.
    denominator: 'container',
  })

  if (isLoading) {
    return (
      <div className="my-8 rounded-xl border border-gray-200 bg-white p-6 shadow-xs">
        <div className="flex items-center justify-center min-h-[200px]">
          <div className="text-center">
            <Loader2 className="w-6 h-6 text-green-600 animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading comparison...</p>
          </div>
        </div>
      </div>
    )
  }

  if (products.length === 0) return null

  const formatPrice = (price: number | string) =>
    typeof price === "string" ? price : price.toFixed(2)

  return (
    <div ref={impressionRef} className="my-8 not-prose">
      <div className="rounded-xl border border-gray-200 bg-white shadow-xs overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-green-600" />
            <span className="text-sm font-semibold text-gray-700">
              Product Comparison
            </span>
          </div>
        </div>

        {/* Desktop: horizontal table */}
        <div className="hidden md:block overflow-x-auto scroll-hint-fade">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {products.map((product) => (
                  <th
                    key={product.id}
                    className="p-4 text-center align-top"
                    style={{ width: `${100 / products.length}%` }}
                  >
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-24 h-24 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                        {product.image ? (
                          <Image
                            src={getDirectusImageUrl(product.image, 192, 192)}
                            alt={product.name}
                            width={96}
                            height={96}
                            loading="lazy"
                            className="w-full h-full object-contain p-2"
                          />
                        ) : (
                          <ShoppingCart className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                      <span className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">
                        {product.name}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Price row */}
              <tr className="border-b border-gray-100">
                {products.map((product) => (
                  <td key={product.id} className="p-4 text-center">
                    <span className="text-lg font-bold text-gray-900">
                      ${formatPrice(product.price)}
                    </span>
                  </td>
                ))}
              </tr>
              {/* Description row */}
              <tr className="border-b border-gray-100">
                {products.map((product) => (
                  <td
                    key={product.id}
                    className="p-4 text-center align-top"
                  >
                    <p className="text-xs text-gray-600 leading-relaxed line-clamp-3">
                      {product.description}
                    </p>
                  </td>
                ))}
              </tr>
              {/* CTA row */}
              <tr>
                {products.map((product) => (
                  <td key={product.id} className="p-4 text-center">
                    <a
                      href={product.affiliate_url || "#"}
                      target="_blank"
                      rel="noopener noreferrer nofollow sponsored"
                      data-affiliate="true"
                      data-affiliate-placement="card-v1"
                      onClick={() => handleProductClick(product)}
                      className="inline-flex items-center gap-1.5 bg-green-700 hover:bg-green-800 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors shadow-xs"
                    >
                      <ShoppingCart className="w-3.5 h-3.5" />
                      Buy on Amazon<span className="sr-only">: {product.name}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards */}
        <div className="md:hidden divide-y divide-gray-100">
          {products.map((product) => (
            <div key={product.id} className="p-4 flex items-start gap-3">
              <div className="w-16 h-16 flex-shrink-0 bg-gray-50 rounded-lg overflow-hidden border border-gray-200 flex items-center justify-center">
                {product.image ? (
                  <Image
                    src={getDirectusImageUrl(product.image, 128, 128)}
                    alt={product.name}
                    width={64}
                    height={64}
                    loading="lazy"
                    className="w-full h-full object-contain p-1"
                  />
                ) : (
                  <ShoppingCart className="w-6 h-6 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 leading-tight line-clamp-2">
                  {product.name}
                </p>
                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-base font-bold text-gray-900">
                    ${formatPrice(product.price)}
                  </span>
                  <a
                    href={product.affiliate_url || "#"}
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    data-affiliate="true"
                    data-affiliate-placement="card-v1"
                    onClick={() => handleProductClick(product)}
                    className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <ShoppingCart className="w-3 h-3" />
                    Buy
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-gray-500">
              As an Amazon Associate we earn from qualifying purchases.{" "}
              <Link
                href="/legal#affiliate-disclaimer"
                className="underline hover:text-gray-700 transition-colors"
              >
                Disclosure
              </Link>
            </p>
            <Link
              href={`/products?category=${encodeURIComponent(effectiveCategory)}${slug ? `&source=comparison-${slug}` : ""}`}
              className="inline-flex flex-shrink-0 items-center gap-1.5 text-xs font-medium text-green-600 hover:text-green-700"
            >
              Browse all
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
